import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useProfile } from "../../../context/ProfileContext";
import { useGroups } from "../../../context/ResourceGroupsContext";
import { useResourceAggregate } from "../../../hooks/useResourceAggregate";
import { useResourceAggregateByGroups } from "../../../hooks/useResourceAggregateByGroups";
import MainPanelContent, {
  CONTENT_MAX_WIDTH,
} from "../components/MainPanelContent";
import GpuResourceCard, { AssignmentBadge } from "./GpuResourceCard";
import {
  ACCENT,
  FONT,
  HAIRLINE,
  INK,
  INK_MUTED,
  INK_SECONDARY,
  RADIUS,
  SURFACE_MUTED,
  WEIGHT,
  withAlpha,
} from "../../../theme/tokens";

function formatSince(seconds) {
  if (seconds == null) return "";
  if (seconds < 60) return `${seconds}s ago`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s ago`;
  const h = Math.floor(m / 60);
  const m2 = m % 60;
  return `${h}h ${m2}m ago`;
}

function formatGroupType(type) {
  if (!type) return null;
  if (type === "open") return "open access";
  return type;
}

// Start with a little assigned capacity visible.
function getInitialExpandedGroupIds(resourceGroups) {
  return resourceGroups
    .filter((group) => group.isAssigned)
    .slice(0, 2)
    .map((group) => group.id);
}

function buildResourceCards(group) {
  return group.resources.map((resource) => {
    const liveSlice = resource.liveSlice;

    return {
      id: resource.id,
      partitionName: resource.partitionName,
      isAssigned: group.isAssigned,
      partitionType: liveSlice?.partition_type ?? null,
      gpuFamily: liveSlice?.gpu_family ?? null,
      gpuMemoryGb: liveSlice?.gpu_memory_gb ?? null,
      cpu: liveSlice?.cpu ?? null,
      memory: liveSlice?.memory ?? null,
      gpu: liveSlice?.gpu ?? null,
      hasLiveData: resource.hasLiveData,
      maxWalltimeInteractive: resource.maxWalltimeInteractive,
      maxWalltimeBatch: resource.maxWalltimeBatch,
    };
  });
}

function GroupTypeLabel({ type }) {
  const label = formatGroupType(type);
  if (!label) return null;
  return (
    <Box
      component="span"
      sx={{
        px: 0.875,
        py: 0.25,
        borderRadius: `${RADIUS.pill}px`,
        bgcolor: SURFACE_MUTED,
        border: `1px solid ${HAIRLINE}`,
        color: INK_SECONDARY,
        fontSize: FONT.xs,
        fontWeight: WEIGHT.semibold,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}

GroupTypeLabel.propTypes = {
  type: PropTypes.string,
};

// Clickable group header with keyboard-accessible expand/collapse.
function ResourceGroupHeader({ group, isExpanded, onToggle }) {
  return (
    <Box
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        cursor: "pointer",
        borderRadius: 1,
        "&:focus-visible": {
          outline: `2px solid ${ACCENT}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: FONT.md,
              fontWeight: WEIGHT.semibold,
              color: group.isAssigned ? INK : INK_SECONDARY,
            }}
          >
            {group.name}
          </Typography>
          <GroupTypeLabel type={group.type} />
          <AssignmentBadge isAssigned={group.isAssigned} />
          {group.expiresAt && (
            <Typography sx={{ fontSize: FONT.xs, color: INK_MUTED }}>
              Expires {new Date(group.expiresAt).toLocaleDateString()}
            </Typography>
          )}
        </Box>
        {group.description && (
          <Typography
            sx={{ fontSize: FONT.sm, color: INK_SECONDARY, mt: 0.375 }}
          >
            {group.description}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", color: INK_MUTED }}>
        {isExpanded ? (
          <KeyboardArrowUpIcon fontSize="small" />
        ) : (
          <KeyboardArrowDownIcon fontSize="small" />
        )}
      </Box>
    </Box>
  );
}

ResourceGroupHeader.propTypes = {
  group: PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string,
    description: PropTypes.string,
    isAssigned: PropTypes.bool.isRequired,
    expiresAt: PropTypes.string,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default function ComputeResources() {
  const [timeSince, setTimeSince] = useState(null);
  const [expandedGroupIds, setExpandedGroupIds] = useState(new Set());
  const initializedExpansionRef = useRef(false);

  const { profile, profileLoading, profileError } = useProfile();
  const {
    groups: allGroups,
    loading: groupsLoading,
    error: groupsError,
  } = useGroups();
  const {
    aggregate,
    error,
    lastUpdated,
    refetch: refetchResources,
  } = useResourceAggregate({
    pollIntervalMs: 20000,
    refreshOnVisible: true,
  });
  const { resourceGroups: resourceGroupRows } = useResourceAggregateByGroups({
    aggregate,
    allGroups,
    userGroups: profile?.groups,
  });

  useEffect(() => {
    const timer = setInterval(() => {
      if (lastUpdated) {
        setTimeSince(Math.floor((Date.now() - lastUpdated) / 1000));
      }
    }, 5000);

    if (lastUpdated) {
      setTimeSince(Math.floor((Date.now() - lastUpdated) / 1000));
    }
    return () => clearInterval(timer);
  }, [lastUpdated]);

  const sinceText = useMemo(() => formatSince(timeSince), [timeSince]);

  const resourceGroups = useMemo(
    () =>
      resourceGroupRows.map((group) => ({
        ...group,
        cards: buildResourceCards(group),
      })),
    [resourceGroupRows],
  );

  const isExpansionDataReady =
    Boolean(aggregate) &&
    !profileLoading &&
    !groupsLoading &&
    resourceGroups.length > 0;

  useEffect(() => {
    if (initializedExpansionRef.current || !isExpansionDataReady) {
      return;
    }

    setExpandedGroupIds(new Set(getInitialExpandedGroupIds(resourceGroups)));
    initializedExpansionRef.current = true;
  }, [isExpansionDataReady, resourceGroups]);

  const toggleExpanded = (groupId) => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const hasGroupData =
    resourceGroups.length > 0 ||
    (allGroups?.length ?? 0) > 0 ||
    (profile?.groups?.length ?? 0) > 0;

  const isInitialAggregateLoading = !aggregate && !error;
  const isInitialGroupLoading =
    (profileLoading || groupsLoading) &&
    !profileError &&
    !groupsError &&
    !hasGroupData;

  if (isInitialAggregateLoading || isInitialGroupLoading) {
    return (
      <MainPanelContent title="Compute Resources" maxWidth={CONTENT_MAX_WIDTH}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ p: 3, height: "100%" }}
        >
          <CircularProgress size={22} />
          <Typography sx={{ ml: 2 }}>Loading Compute Resources…</Typography>
        </Box>
      </MainPanelContent>
    );
  }

  if (error && !aggregate) {
    return (
      <MainPanelContent title="Compute Resources" maxWidth={CONTENT_MAX_WIDTH}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={refetchResources}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </MainPanelContent>
    );
  }

  const hasAssignedGroup = resourceGroups.some((group) => group.isAssigned);

  return (
    <MainPanelContent
      title="Compute Resources"
      maxWidth={CONTENT_MAX_WIDTH}
      rightNode={
        sinceText && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
            aria-live="polite"
          >
            Last updated: {sinceText}
          </Typography>
        )
      }
      subtitle={
        "Compute resources are organized into groups — each grants access " +
        "to one or more partitions with configured time limits. Expand a group to see " +
        "live CPU, GPU, and memory usage."
      }
      contentGap={4}
      headerMb={1.5}
    >
      {error && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={refetchResources}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {profileError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {profileError}
        </Alert>
      )}

      {groupsError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {groupsError.message || "Failed to load resource groups."}
        </Alert>
      )}

      {!hasAssignedGroup && (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          You aren&apos;t assigned to any resource groups yet — browse
          what&apos;s available below, or ask an admin for access.
        </Alert>
      )}

      {resourceGroups.length === 0 ? (
        <Alert severity="info">No resource groups found.</Alert>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {resourceGroups.map((group) => {
            const isExpanded = expandedGroupIds.has(group.id);

            return (
              <Box
                key={group.id}
                sx={{
                  borderRadius: `${RADIUS.md}px`,
                  border: `1px solid ${group.isAssigned ? withAlpha(ACCENT, 0.28) : HAIRLINE}`,
                  bgcolor: group.isAssigned
                    ? withAlpha(ACCENT, 0.035)
                    : SURFACE_MUTED,
                  p: 2,
                }}
              >
                <ResourceGroupHeader
                  group={group}
                  isExpanded={isExpanded}
                  onToggle={() => toggleExpanded(group.id)}
                />
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(auto-fill, minmax(300px, 1fr))",
                      },
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    {group.cards.map((card) => (
                      <GpuResourceCard key={card.id} card={card} />
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}
    </MainPanelContent>
  );
}
